import React, { useState } from 'react';
import { CircularProgress, IconButton, ListItemText, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addMapSystem, fetchMaps } from '../api/maps';
import { fetchSystemVaults } from '../api/systems';
import { C, FONT } from '../theme/tokens';

/**
 * Deposit this system into a vault, from the system's own page.
 *
 * The page it lives on is a dead end otherwise: a member reads the dossier,
 * decides the hole is worth keeping, and the only two things they can do
 * here are star it and set destination. Actually filing it meant navigating
 * to /vault, picking the vault, and searching for the same system they were
 * already looking at — so in practice people pasted the name into Discord
 * instead, and the vault stayed emptier than the corp's actual knowledge.
 *
 * Vaults that already hold the system are listed but disabled rather than
 * hidden, because "it is already filed there" is the answer to the question
 * being asked, and silently omitting them looks like the vault is missing.
 */
const DepositToVaultButton: React.FC<{ solarSystemId: number; systemName: string }> = ({
  solarSystemId,
  systemName,
}) => {
  const queryClient = useQueryClient();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  const vaults = useQuery({ queryKey: ['maps'], queryFn: fetchMaps, enabled: !!anchor });
  const holding = useQuery({
    queryKey: ['system-vaults', solarSystemId],
    queryFn: () => fetchSystemVaults(solarSystemId),
    enabled: !!anchor,
  });

  const deposit = useMutation({
    mutationFn: (vaultId: number) => addMapSystem(vaultId, { solarSystemId }),
    onSuccess: (_row, vaultId) => {
      // Everything that counts entries or lists this system is now stale —
      // the same set the vault page invalidates for its own writes.
      queryClient.invalidateQueries({ queryKey: ['map-systems', vaultId] });
      queryClient.invalidateQueries({ queryKey: ['maps'] });
      queryClient.invalidateQueries({ queryKey: ['system-vaults', solarSystemId] });
      setAnchor(null);
    },
  });

  const alreadyIn = new Set((holding.data ?? []).map((v) => v.vaultId));
  const loading = vaults.isLoading || holding.isLoading;

  return (
    <>
      <Tooltip title={`Save ${systemName} to a vault`}>
        <IconButton
          size="small"
          aria-label={`Save ${systemName} to a vault`}
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ color: C.wormhole }}
        >
          <LibraryAddIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {loading && (
          <MenuItem disabled>
            <CircularProgress size={16} sx={{ color: C.wormhole, mr: 1 }} /> Loading vaults…
          </MenuItem>
        )}

        {vaults.isError && (
          <MenuItem disabled>
            <Typography sx={{ color: C.gold, fontSize: '0.8rem' }}>Could not load vaults</Typography>
          </MenuItem>
        )}

        {vaults.data?.length === 0 && (
          <MenuItem disabled>
            <Typography sx={{ color: C.textDim, fontSize: '0.8rem' }}>No vaults yet — create one in Data Vault</Typography>
          </MenuItem>
        )}

        {vaults.data?.map((vault) => {
          const held = alreadyIn.has(vault.id);
          return (
            <MenuItem
              key={vault.id}
              disabled={held || deposit.isPending}
              onClick={() => deposit.mutate(vault.id)}
              sx={{ fontFamily: FONT.mono, fontSize: '0.82rem' }}
            >
              <ListItemText primary={vault.name} secondary={held ? 'already filed here' : undefined} />
            </MenuItem>
          );
        })}

        {deposit.isError && (
          <MenuItem disabled>
            <Typography sx={{ color: C.gold, fontSize: '0.8rem' }}>Save failed — try again</Typography>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default DepositToVaultButton;
